import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AlertService } from '../components/alert/alert.svc';
import { QuickBooksSyncAdminData } from './quickbooks-sync-admin.data';

@Component({
  selector: 'quickbooks-sync-admin',
  templateUrl: './quickbooks-sync-admin.component.html',
  styleUrls: ['./quickbooks-sync-admin.component.scss']
})
export class QuickBooksSyncAdminComponent implements OnInit, OnDestroy {
  form: FormGroup;
  requestId: string;
  status: any;
  loading = false;
  controlPending = false;
  private statusTimer: any;
  private terminalStatusReported = false;

  constructor(
    private formBuilder: FormBuilder,
    private data: QuickBooksSyncAdminData,
    private alert: AlertService
  ) { }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      minimumPaymentDate: [this.toDateInputValue(new Date(2026, 7, 1)), Validators.required],
      includeFuturePayments: [false],
      includeNonSuccessfulPayments: [false],
      isProduction: [true]
    });

    this.data.getActive().subscribe((active: any) => {
      if (active && active.requestId) {
        this.requestId = active.requestId;
        this.status = active;
        this.startPolling();
      }
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  start(): void {
    if (this.form.invalid || !confirm('להתחיל סנכרון של כל אנשי הקשר מול QuickBooks?')) {
      return;
    }

    this.loading = true;
    this.terminalStatusReported = false;
    const payload = this.form.getRawValue();
    payload.minimumPaymentDate = payload.minimumPaymentDate ? new Date(`${payload.minimumPaymentDate}T00:00:00`) : null;

    this.data.start(payload).subscribe((response: any) => {
      this.loading = false;
      this.requestId = response.requestId;
      this.status = { requestId: this.requestId, status: 'pending', totalContacts: 0, processedContacts: 0 };
      this.alert.info(response.message || 'בקשת הסנכרון נוספה לתור.', 6);
      this.startPolling();
    }, () => {
      this.loading = false;
    });
  }

  pause(): void {
    this.sendControl('pause');
  }

  resume(): void {
    this.sendControl('resume');
  }

  stop(): void {
    if (confirm('לעצור את הסנכרון לאחר סיום איש הקשר הנוכחי?')) {
      this.sendControl('stop');
    }
  }

  get progressPercent(): number {
    const total = this.status && this.status.totalContacts ? this.status.totalContacts : 0;
    const processed = this.status && this.status.processedContacts ? this.status.processedContacts : 0;
    return total > 0 ? Math.min(100, Math.round(processed * 100 / total)) : 0;
  }

  get isActive(): boolean {
    return !!this.status && ['pending', 'processing', 'paused'].indexOf(this.status.status) >= 0;
  }

  get canPause(): boolean {
    return this.isActive && this.status.status !== 'paused' && this.status.requestedCommand !== 'pause';
  }

  get canResume(): boolean {
    return this.isActive && (this.status.status === 'paused' || this.status.requestedCommand === 'pause');
  }

  statusLabel(value: string): string {
    const labels: any = {
      pending: 'ממתין בתור',
      processing: 'פעיל',
      paused: 'מושהה',
      stopped: 'נעצר',
      completed: 'הושלם',
      failed: 'נכשל',
      notfound: 'לא נמצא'
    };
    return labels[value] || value || 'לא פעיל';
  }

  private sendControl(command: string): void {
    if (!this.requestId || this.controlPending) {
      return;
    }

    this.controlPending = true;
    this.data.control(this.requestId, command).subscribe(() => {
      this.controlPending = false;
      if (this.status) {
        this.status.requestedCommand = command;
      }
      this.pollStatus();
    }, () => {
      this.controlPending = false;
    });
  }

  private startPolling(): void {
    this.stopPolling();
    this.terminalStatusReported = false;
    this.pollStatus();
    this.statusTimer = setInterval(() => this.pollStatus(), 2000);
  }

  private stopPolling(): void {
    if (this.statusTimer) {
      clearInterval(this.statusTimer);
      this.statusTimer = null;
    }
  }

  private pollStatus(): void {
    if (!this.requestId) {
      return;
    }

    this.data.getStatus(this.requestId).subscribe((status: any) => {
      this.status = status;
      if (['completed', 'failed', 'stopped'].indexOf(status.status) >= 0) {
        this.stopPolling();
        if (!this.terminalStatusReported) {
          this.terminalStatusReported = true;
          const message = status.lastMessage || status.summary || 'סנכרון QuickBooks הסתיים.';
          if (status.status === 'failed' || (status.failedContacts || 0) > 0) {
            this.alert.error(message, 12);
          } else if ((status.failedPayments || 0) > 0) {
            this.alert.info(message, 10);
          } else if (status.status === 'completed') {
            this.alert.success(message, 8);
          } else {
            this.alert.info(message, 8);
          }
        }
      }
    });
  }

  private toDateInputValue(date: Date): string {
    const month = (`0${date.getMonth() + 1}`).slice(-2);
    const day = (`0${date.getDate()}`).slice(-2);
    return `${date.getFullYear()}-${month}-${day}`;
  }
}
